import React, { useEffect, useState } from 'react';
import { CheckCircle2, XCircle, Loader2, ShieldCheck, ClipboardCheck, IndianRupee, AlertOctagon } from 'lucide-react';
import AdminLayout from '../../components/AdminLayout';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authCfg = () => {
  const token = localStorage.getItem('c24_token') || localStorage.getItem('c24_admin_token');
  return { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} };
};

const BUCKETS = [
  { id: 'expenses',   label: 'Expenses',   color: '#EA580C', bg: '#FFEDD5' },
  { id: 'incentives', label: 'Incentives', color: '#7B1FA2', bg: '#F3E5F5' },
  { id: 'salaries',   label: 'Salaries',   color: '#0891B2', bg: '#CFFAFE' },
  { id: 'refunds',    label: 'Refunds',    color: '#DC2626', bg: '#FEE2E2' },
];

const AdminApprovals = () => {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('pending');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/approvals?status=${status}`, authCfg());
      setData(data);
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.detail || 'Failed to load' });
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const decide = async (id, action) => {
    const reason = window.prompt(`${action === 'approve' ? 'Approve' : 'Reject'} entry — enter reason/note:`, '');
    if (reason === null) return;
    setBusy(true);
    try {
      await axios.post(`${API}/admin/approvals/${id}/${action}`, { reason }, authCfg());
      setMsg({ ok: true, text: `${action === 'approve' ? 'Approved' : 'Rejected'}: ${id}` });
      load();
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.detail || 'Failed' });
    } finally { setBusy(false); }
  };

  return (
    <AdminLayout title="Pending Approvals">
      {msg && (
        <div data-testid="approval-msg" className={`mb-4 text-sm rounded-lg px-3 py-2 ${msg.ok ? 'bg-[#E8F5E9] text-[#1B5E20]' : 'bg-red-50 text-red-700'}`}>{msg.text}</div>
      )}
      <div className="flex gap-2 mb-4">
        {['pending', 'approved', 'rejected', 'all'].map((s) => (
          <button key={s} data-testid={`status-tab-${s}`} onClick={() => setStatus(s)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold ${status === s ? 'bg-[#2E7D32] text-white' : 'bg-white border border-neutral-200 text-neutral-600 hover:border-[#2E7D32]'}`}>
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>

      {!data ? (
        <div className="text-center py-16"><Loader2 className="w-6 h-6 animate-spin mx-auto text-neutral-400" /></div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            {BUCKETS.map((b) => (
              <div key={b.id} data-testid={`approval-count-${b.id}`} className="bg-white border border-neutral-200 rounded-xl p-4">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: b.bg }}>
                  <ClipboardCheck className="w-5 h-5" style={{ color: b.color }} />
                </div>
                <p className="mt-3 text-[11px] uppercase tracking-wider text-neutral-500 font-semibold">{b.label}</p>
                <p className="font-extrabold text-2xl mt-0.5">{data.counts[b.id] || 0}</p>
              </div>
            ))}
          </div>

          {BUCKETS.map((b) => {
            const items = data.items[b.id] || [];
            if (!items.length) return null;
            return (
              <div key={b.id} className="mb-6 bg-white border border-neutral-200 rounded-2xl" data-testid={`approval-section-${b.id}`}>
                <div className="px-4 py-3 border-b border-neutral-100"><p className="font-bold text-sm">{b.label} — {items.length}</p></div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-neutral-50 border-b border-neutral-200 text-left">
                      <tr>
                        <th className="px-4 py-2 text-xs font-bold text-neutral-500 uppercase">Date</th>
                        <th className="px-4 py-2 text-xs font-bold text-neutral-500 uppercase">Franchise</th>
                        <th className="px-4 py-2 text-xs font-bold text-neutral-500 uppercase">Category</th>
                        <th className="px-4 py-2 text-xs font-bold text-neutral-500 uppercase">Note</th>
                        <th className="px-4 py-2 text-xs font-bold text-neutral-500 uppercase text-right">Amount</th>
                        <th className="px-4 py-2 text-xs font-bold text-neutral-500 uppercase text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((r) => (
                        <tr key={r.id} data-testid={`approval-row-${r.id}`} className="border-b border-neutral-100 hover:bg-neutral-50">
                          <td className="px-4 py-2.5 text-xs">{(r.created_at || '').slice(0, 10)}</td>
                          <td className="px-4 py-2.5 text-xs">
                            <p className="font-semibold">{r.district || '—'}</p>
                            <p className="text-[10px] text-neutral-500 font-mono">{r.franchise_id}</p>
                          </td>
                          <td className="px-4 py-2.5 text-xs capitalize">{(r.category || '').replace(/_/g, ' ')}</td>
                          <td className="px-4 py-2.5 text-xs text-neutral-600 max-w-md truncate">{r.note || r.employee_name || '—'}</td>
                          <td className="px-4 py-2.5 text-right font-bold text-xs text-[#EA580C]">₹{(r.amount || 0).toLocaleString('en-IN')}</td>
                          <td className="px-4 py-2.5 text-right whitespace-nowrap">
                            {status === 'pending' ? (
                              <>
                                <button data-testid={`approve-btn-${r.id}`} onClick={() => decide(r.id, 'approve')} disabled={busy}
                                  className="inline-flex items-center gap-1 bg-[#2E7D32] hover:bg-[#276D2A] disabled:opacity-40 text-white text-xs font-semibold px-2.5 py-1 rounded-lg mr-1">
                                  <CheckCircle2 className="w-3 h-3" /> Approve
                                </button>
                                <button data-testid={`reject-btn-${r.id}`} onClick={() => decide(r.id, 'reject')} disabled={busy}
                                  className="inline-flex items-center gap-1 bg-white border border-red-200 hover:bg-red-50 text-red-600 text-xs font-semibold px-2.5 py-1 rounded-lg">
                                  <XCircle className="w-3 h-3" /> Reject
                                </button>
                              </>
                            ) : (
                              <span className={`text-xs font-bold ${r.approval_status === 'approved' ? 'text-[#2E7D32]' : 'text-red-600'}`}>{r.approval_status}</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}

          {Object.values(data.counts).reduce((a, b) => a + b, 0) === 0 && (
            <div className="bg-white border border-dashed border-neutral-200 rounded-2xl p-12 text-center">
              <ShieldCheck className="w-10 h-10 text-neutral-300 mx-auto" />
              <p className="mt-2 text-sm font-semibold text-neutral-700">No {status} items</p>
              <p className="text-xs text-neutral-500 mt-1">Franchise-side actions crossing the ₹10,000 threshold or requiring HQ approval appear here.</p>
            </div>
          )}
        </>
      )}
    </AdminLayout>
  );
};

export default AdminApprovals;
